import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, ExternalLink } from "lucide-react";
import { SITE } from "@/lib/site";
import { CATEGORIES, getCategory, modulesIn } from "@/lib/modules";
import { Container, Section, SectionHeader, Reveal, CTAButton, Kicker, useSeo } from "@/components/ui-kit";
import { ProductFrame } from "@/components/mockups/ProductFrame";
import { CTABand } from "@/components/sections/Social";
import { cn } from "@/lib/utils";

const CategoryPage = () => {
  const { slug } = useParams();
  const cat = getCategory(slug);
  useSeo({ title: cat ? `${cat.name} — ${cat.headline}` : "Product", description: cat?.desc, path: `/platform/${slug}`, image: `/og/cat-${slug}.png` });
  if (!cat) return <Navigate to="/product" replace />;
  const mods = modulesIn(slug);

  return (
    <div data-testid={`category-page-${slug}`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-60" aria-hidden />
        <div className={cn("orb h-[460px] w-[460px] -top-40 right-[-100px]", cat.color === "lime" ? "bg-mk-lime/15" : "bg-mk-blue/25")} aria-hidden />
        <Container className="relative pt-12 pb-14 lg:pt-16 lg:pb-20">
          <nav className="flex items-center gap-1.5 text-xs text-mk-muted" aria-label="Breadcrumb"><Link to="/product" className="hover:text-mk-text">Product</Link><ChevronRight className="h-3 w-3" /><span className="text-mk-dim">{cat.name}</span></nav>
          <div className="mt-8 max-w-3xl">
            <Kicker tone={cat.color}>{cat.kicker} · {mods.length} modules</Kicker>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-mk-text leading-[1.02] text-balance">{cat.headline}</h1>
            <p className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed">{cat.desc}</p>
          </div>
          <div className="mt-8 flex flex-wrap gap-2" data-testid="category-jumplinks">
            {mods.map((m) => <a key={m.slug} href={`#${m.slug}`} data-testid={`category-jump-${m.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-mk-border bg-mk-surface px-3 py-1.5 text-xs text-mk-dim hover:text-mk-text hover:border-mk-lime/50 transition-colors"><m.icon className="h-3.5 w-3.5 text-mk-lime" />{m.name}</a>)}
          </div>
          <div className="mt-6 flex flex-wrap gap-2 text-xs text-mk-muted">
            {CATEGORIES.filter((c) => c.slug !== slug).map((c) => <Link key={c.slug} to={`/platform/${c.slug}`} className="hover:text-mk-lime transition-colors">→ {c.name}</Link>)}
          </div>
        </Container>
      </section>

      {mods.map((m, i) => (
        <Section key={m.slug} id={m.slug} className={cn("scroll-mt-24", i % 2 ? "bg-mk-surface/40 border-y border-mk-border" : "")} data-testid={`category-module-${m.slug}`}>
          <Container>
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className={cn("lg:col-span-5 min-w-0", i % 2 && "lg:order-2")}>
                <div className="flex items-center gap-3"><span className="grid place-items-center h-11 w-11 rounded-xl border border-mk-border bg-mk-surface text-mk-lime"><m.icon className="h-5 w-5" /></span><span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mk-muted">{m.kicker}</span></div>
                <h2 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight text-mk-text text-balance">{m.name}</h2>
                <p className="mt-2 font-display text-lg text-mk-lime">{m.headline}</p>
                <p className="mt-4 text-mk-dim leading-relaxed">{m.summary}</p>
                <ul className="mt-5 space-y-2">{m.bullets.map((b) => <li key={b} className="flex items-start gap-2.5 text-sm text-mk-text/90"><Check className="h-4 w-4 text-mk-lime shrink-0 mt-0.5" />{b}</li>)}</ul>
                <div className="mt-6 flex flex-wrap gap-3">
                  <CTAButton to={`/product/${m.slug}`} variant="ghost" testId={`category-deepdive-${m.slug}`}>Deep dive <ArrowRight className="h-4 w-4" /></CTAButton>
                  <a href={SITE.signupUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-mk-dim hover:text-mk-lime transition-colors py-3">Try it in the sandbox <ExternalLink className="h-3.5 w-3.5" /></a>
                </div>
              </div>
              <Reveal className={cn("lg:col-span-7 min-w-0", i % 2 && "lg:order-1")}>
                <ProductFrame shot={m.shot} mockup={m.mockup} url={m.liveUrl?.replace("https://", "")} alt={`${m.name} in Fixlr`} testId={`category-frame-${m.slug}`} />
              </Reveal>
            </div>
          </Container>
        </Section>
      ))}

      <CTABand title={`Ready to run ${cat.name.toLowerCase()} on Fixlr?`} />
    </div>
  );
};

export default CategoryPage;
